import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

export type ClipCardSelectOption = {
  value: string;
  label: string;
  /** Optional leading status dot color. */
  dotColor?: string;
};

type Props = {
  /** Accessible label for the control (e.g. "Status", "Assignee"); not shown. */
  ariaLabel: string;
  /** Currently selected option value. */
  value?: string;
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  options: ClipCardSelectOption[];
  /**
   * Persists the selection. Return `false` (or reject) to signal the save
   * failed so the optimistic value is rolled back to the previous selection.
   */
  onSelect: (value: string) => void | Promise<boolean>;
  /** Message shown inside the menu when there are no options. */
  emptyText?: string;
};

type MenuCoords = {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
};

const ROW_HEIGHT = 34;
const MENU_MAX_HEIGHT = 220;

/**
 * Compact dropdown for the Content Studio clip card (Status / Assignee). The
 * menu is rendered in a portal so it escapes the card's `overflow: hidden`
 * (which would otherwise clip it), and flips above/below the trigger based on
 * available space. Selection is optimistic so the trigger updates instantly,
 * before the async Dato write lands the value back on `value`.
 */
const ClipCardSelect = ({
  ariaLabel,
  value,
  placeholder = 'Select',
  options,
  onSelect,
  emptyText = 'None available',
}: Props) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const [pending, setPending] = useState<string | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Clear the optimistic value once the prop catches up (or changes externally).
  useEffect(() => {
    setPending(undefined);
  }, [value]);

  // While open: close on outside click (accounting for the portaled menu),
  // scroll, or resize. The trigger and the portaled menu both count as "inside".
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onReposition = (e: Event) => {
      // The capture-phase scroll listener fires for any scroll in the document,
      // including the menu's own (overflow-y: auto). A scroll inside the menu
      // shouldn't close it — only page/ancestor scrolls that detach the
      // fixed-position menu from its trigger should.
      if (e.type === 'scroll' && menuRef.current?.contains(e.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  const effectiveValue = pending ?? value;
  const selected = options.find(o => o.value === effectiveValue);

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = Math.min(
      options.length * ROW_HEIGHT + 8,
      MENU_MAX_HEIGHT,
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight + 8 && rect.top > spaceBelow;
    setCoords({
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
    setOpen(true);
  };

  const handleSelect = async (next: string) => {
    setPending(next);
    setOpen(false);
    // Optimistic: the trigger shows `next` immediately. On success the `value`
    // prop catches up and the effect above clears `pending` (no flicker). If the
    // save reports failure, roll back so we don't strand a value that never
    // persisted.
    try {
      const ok = await onSelect(next);
      if (ok === false) setPending(undefined);
    } catch {
      setPending(undefined);
    }
  };

  return (
    <Wrapper onClick={e => e.stopPropagation()}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        $open={open}
        onClick={toggleOpen}
      >
        <SelectedValue>
          {selected?.dotColor && <Dot $color={selected.dotColor} />}
          {/* Fall back to the raw value (e.g. an assignee email) when options
              haven't loaded yet or no longer contain it, so a saved value still
              shows rather than reverting to the placeholder. */}
          <span>
            {selected ? selected.label : effectiveValue || placeholder}
          </span>
        </SelectedValue>
        <ChevronDownIcon width="10" height="10" strokeColor="currentColor" />
      </Trigger>
      {open &&
        coords &&
        createPortal(
          <Menu
            ref={menuRef}
            onClick={e => e.stopPropagation()}
            style={{
              left: coords.left,
              width: coords.width,
              ...(coords.top != null ? { top: coords.top } : {}),
              ...(coords.bottom != null ? { bottom: coords.bottom } : {}),
            }}
          >
            {options.length === 0 ? (
              <EmptyItem>{emptyText}</EmptyItem>
            ) : (
              options.map(o => (
                <MenuItem
                  key={o.value}
                  type="button"
                  $active={o.value === effectiveValue}
                  onClick={() => handleSelect(o.value)}
                >
                  {o.dotColor && <Dot $color={o.dotColor} />}
                  <span>{o.label}</span>
                </MenuItem>
              ))
            )}
          </Menu>,
          document.body,
        )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
`;

const Trigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  padding: 5px 8px;
  border-radius: 6px;
  /* One subtle step above the card (--surface) rather than below it — sitting
     darker than the card read as a cut-out hole; sitting just above it blends. */
  background: var(--surface-elevated);
  /* Borderless at rest for a sleeker look; transparent border keeps layout
     stable while the accent outline shows on hover/open. */
  border: 1px solid ${props => (props.$open ? 'var(--accent)' : 'transparent')};
  /* Secondary grey rather than primary white — the status/assignee row is card
     metadata, not a title, so it shouldn't compete with the clip name. */
  color: var(--text-secondary);
  font-size: var(--text-xs);
  cursor: pointer;
  transition:
    border-color var(--transition-fast) ease,
    background var(--transition-fast) ease;

  &:hover {
    /* Translucent white lift, same hover token as the menu items below. */
    background: var(--sidebar-hover);
    border-color: ${props => (props.$open ? 'var(--accent)' : 'var(--border)')};
  }

  svg {
    flex-shrink: 0;
    color: var(--text-secondary);
  }
`;

const SelectedValue = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Dot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const Menu = styled.div`
  position: fixed;
  z-index: 100001;
  display: flex;
  flex-direction: column;
  max-height: ${MENU_MAX_HEIGHT}px;
  overflow-y: auto;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  box-shadow: var(--elevation-md);
`;

const MenuItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: ${props =>
    props.$active ? 'var(--sidebar-hover)' : 'transparent'};
  color: var(--text-secondary);
  font-size: var(--text-xs);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: all var(--transition-fast);

  ${props =>
    props.$active &&
    css`
      color: var(--foreground);
    `}

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: var(--sidebar-hover);
    color: var(--foreground);
  }
`;

const EmptyItem = styled.div`
  padding: 8px;
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

export default ClipCardSelect;
